'use client';

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  applyDesignThemeCssVariables,
  DEFAULT_DESIGN_THEME_PREFERENCE,
  DESIGN_THEME_STORAGE_KEY,
  DESIGN_THEME_TOKENS_STORAGE_KEY,
  DesignThemeId,
  DesignThemeMode,
  DesignThemePreference,
  getDesignThemeTokenCache,
  parseDesignThemePreference,
  ResolvedDesignThemeMode,
} from '@/theme/design-themes';

interface DesignThemeContextValue {
  preference: DesignThemePreference;
  resolvedMode: ResolvedDesignThemeMode;
  setThemeId: (themeId: DesignThemeId) => void;
  setMode: (mode: DesignThemeMode) => void;
}

interface DesignThemeState {
  preference: DesignThemePreference;
  resolvedMode: ResolvedDesignThemeMode;
}

const DesignThemeContext = createContext<DesignThemeContextValue | null>(null);

function getSystemMode(): ResolvedDesignThemeMode {
  if (typeof window === 'undefined') {
    return 'light';
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getStoredPreference(): DesignThemePreference {
  if (typeof window === 'undefined') {
    return DEFAULT_DESIGN_THEME_PREFERENCE;
  }

  try {
    const stored = window.localStorage.getItem(DESIGN_THEME_STORAGE_KEY);
    return parseDesignThemePreference(stored ? JSON.parse(stored) : null);
  } catch {
    return DEFAULT_DESIGN_THEME_PREFERENCE;
  }
}

function resolveMode(
  mode: DesignThemeMode,
  systemMode: ResolvedDesignThemeMode
): ResolvedDesignThemeMode {
  return mode === 'system' ? systemMode : mode;
}

function applyThemePreference(
  preference: DesignThemePreference,
  resolvedMode: ResolvedDesignThemeMode
) {
  const root = document.documentElement;

  root.setAttribute('data-attune-theme', preference.themeId);
  root.setAttribute('data-attune-mode', resolvedMode);
  root.setAttribute('data-attune-theme-mode', preference.mode);
  root.setAttribute('data-color-mode', resolvedMode);
  root.setAttribute('data-theme', resolvedMode);
  root.style.colorScheme = resolvedMode;
  root.classList.toggle('dark', resolvedMode === 'dark');
  root.classList.toggle('light', resolvedMode === 'light');
  applyDesignThemeCssVariables(root, preference.themeId, resolvedMode);
  void window.attuneDesktop
    ?.setTheme?.({
      background: getDesignThemeTokenCache(preference.themeId)[resolvedMode].bg,
      mode: resolvedMode,
    })
    .catch(() => {});
}

function persistThemePreference(preference: DesignThemePreference) {
  window.localStorage.setItem(DESIGN_THEME_STORAGE_KEY, JSON.stringify(preference));
  window.localStorage.setItem(
    DESIGN_THEME_TOKENS_STORAGE_KEY,
    JSON.stringify({
      themeId: preference.themeId,
      ...getDesignThemeTokenCache(preference.themeId),
    })
  );
}

export function DesignThemeProvider({ children }: { children: ReactNode }) {
  // SSR + first client paint: keep state at defaults so markup matches the server. Apply the real
  // stored preference in useLayoutEffect (after the init script) so we never run a useEffect with
  // default state that would overwrite localStorage or flash the wrong selection in React state.
  const [themeState, setThemeState] = useState<DesignThemeState>(() => ({
    preference: DEFAULT_DESIGN_THEME_PREFERENCE,
    resolvedMode: 'light',
  }));
  const skipNextPersistEffect = useRef(true);
  const { preference, resolvedMode } = themeState;

  useLayoutEffect(() => {
    const nextPreference = getStoredPreference();
    const nextSystemMode = getSystemMode();
    const nextResolvedMode = resolveMode(nextPreference.mode, nextSystemMode);
    setThemeState({ preference: nextPreference, resolvedMode: nextResolvedMode });
    applyThemePreference(nextPreference, nextResolvedMode);
    try {
      persistThemePreference(nextPreference);
    } catch {
      // localStorage can be unavailable in private browsing or hardened environments.
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const nextSystemMode = mediaQuery.matches ? 'dark' : 'light';
      setThemeState(current => {
        const nextResolvedMode = resolveMode(current.preference.mode, nextSystemMode);
        return nextResolvedMode === current.resolvedMode
          ? current
          : { ...current, resolvedMode: nextResolvedMode };
      });
    };

    handleChange();
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (skipNextPersistEffect.current) {
      skipNextPersistEffect.current = false;
      return;
    }

    applyThemePreference(preference, resolvedMode);

    try {
      persistThemePreference(preference);
    } catch {
      // localStorage can be unavailable in private browsing or hardened environments.
    }
  }, [preference, resolvedMode]);

  const setThemeId = useCallback((themeId: DesignThemeId) => {
    setThemeState(current => ({
      ...current,
      preference: { ...current.preference, themeId },
    }));
  }, []);

  const setMode = useCallback((mode: DesignThemeMode) => {
    setThemeState(current => ({
      preference: { ...current.preference, mode },
      resolvedMode: resolveMode(mode, getSystemMode()),
    }));
  }, []);

  const value = useMemo<DesignThemeContextValue>(
    () => ({
      preference,
      resolvedMode,
      setThemeId,
      setMode,
    }),
    [preference, resolvedMode, setMode, setThemeId]
  );

  return <DesignThemeContext.Provider value={value}>{children}</DesignThemeContext.Provider>;
}

export function useDesignTheme() {
  const context = useContext(DesignThemeContext);

  if (!context) {
    throw new Error('useDesignTheme must be used within DesignThemeProvider');
  }

  return context;
}
